import { useState } from 'react';
import { Check, Copy, Sparkles } from 'lucide-react';
import { useMergeFieldReference } from './hooks';
import type { MergeFieldReference } from './types';

/**
 * "Draft a template with AI" helper on the Templates page. Copies a ready-made
 * prompt listing the exact merge tokens the app fills (from the backend, so it
 * can't drift) — the author pastes it into an AI and appends what they want, and
 * the result uses only resolvable {{tokens}} with no manual wiring.
 */
export function MergeFieldGuide() {
  const { data } = useMergeFieldReference();
  const [copied, setCopied] = useState(false);

  if (!data) return null;
  const prompt = buildPrompt(data);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context / permissions) — the details list
      // below is still readable so the author can copy tokens manually.
    }
  };

  return (
    <section className="bg-sky-50 border border-sky-100 rounded-xl p-6 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Sparkles size={18} className="text-sky-600" /> Draft a template with AI
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Click the “Copy AI prompt” button. Paste the prompt into ChatGPT or Claude and add
            what you want at the end. Overwrite the last section of the prompt with what you want
            (e.g. “New Service Agreement”). It tells the AI exactly which merge fields the app fills
            in, so the template file created from the prompt needs no manual mappings of the{' '}
            <code>&#123;&#123;tokens&#125;&#125;</code> and supported merge fields.
          </p>
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="shrink-0 inline-flex items-center gap-1 bg-sky-600 text-white px-3 py-2 rounded hover:bg-sky-700 text-sm"
        >
          {copied ? (
            <>
              <Check size={14} /> Copied
            </>
          ) : (
            <>
              <Copy size={14} /> Copy AI prompt
            </>
          )}
        </button>
      </div>

      <details className="text-sm">
        <summary className="cursor-pointer text-slate-600 hover:text-slate-800">
          Supported merge fields ({data.fields.length})
        </summary>
        <table className="w-full mt-2">
          <tbody>
            {data.fields.map((f) => (
              <tr key={f.token} className="border-t border-sky-100 align-top">
                <td className="py-1.5 pr-3 font-mono text-xs whitespace-nowrap text-slate-800">
                  {`{{${f.token}}}`}
                </td>
                <td className="py-1.5 pr-3 text-slate-600">{f.description}</td>
                <td className="py-1.5 text-slate-400 text-xs whitespace-nowrap">e.g. {f.example}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </section>
  );
}

function buildPrompt(ref: MergeFieldReference): string {
  const tokenLines = ref.fields.map((f) => `- {{${f.token}}} — ${f.description} (e.g. "${f.example}")`);
  return [
    "I'm creating a document template for my business. The app I use fills in data by replacing {{Token}} placeholders when the document is sent to a customer.",
    '',
    'Use ONLY the merge tokens listed below. Write each one exactly as shown — double curly braces, exact spelling and capitalization. Do NOT invent any other {{placeholders}}: any token not in this list will NOT be filled in.',
    '',
    'Available merge tokens:',
    ...tokenLines,
    '',
    'Rules:',
    `- "RecordNames" lists the customer's ${ref.recordPlural.toLowerCase()} by name.`,
    '- If you need information none of these tokens cover, leave a blank or a bracketed instruction (e.g. "[ enter deposit amount ]") for me to fill in by hand — do not invent a token.',
    '- Output clean, paste-ready text suitable for a Word (.docx) document, with the tokens inline where the data should appear.',
    '',
    'Here is the document I want you to write:',
    '<describe your document here — e.g. "a pet-boarding service agreement with sections for emergency contact, vet authorization, and payment terms">'
  ].join('\n');
}
